import React, { FC, useState, useEffect } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { useQuery } from 'react-query';
import makeStyles from '@mui/styles/makeStyles';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  getUnitsOfMeasure,
  putPartGeneralInfo,
  getPartClassParents,
  createPartsFile,
  getPartFile
} from '../../../../fetch';
import { IDropdownResponse, IPartGeneralInfo, IPartClasses } from '../../../../models';
import { Loader, SMBSelect, ManyImagesUpload } from '../../../../components';
import { ManagePartClassDropdown } from './manage-part-class-dropdown';
import { theme } from '../../../../styles/theme';
import { ALLOWED_IMAGE_EXTENSIONS } from '../../../../helpers';

interface IGeneralInformation {
  generalInfo: IPartGeneralInfo;
  isLoadingGeneralInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchGeneralInfo: () => void;
}

const GeneralInfoSchema = Yup.object().shape({
  partClassId: Yup.number(),
  name: Yup.string(),
  unitOfMeasureId: Yup.number(),
  isActive: Yup.string(),
  quantityInterval: Yup.number(),
  roundUp: Yup.string(),
  partClasses: Yup.array()
});

export const GeneralInformation: FC<IGeneralInformation> = ({
  generalInfo,
  isLoadingGeneralInfo,
  setDirtyForm,
  refetchGeneralInfo
}) => {
  const classes = useStyles();

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { partId }: { partId: string } = useParams();
  const [isUploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [selectedPartClassIdFilter, setSelectedPartClassIdFilter] = useState(null);
  const [selectionsCleared, setSelectionsCleared] = useState<boolean>(false);
  const [selectedValues, setSelectedValues] = useState<IPartClasses[]>(() => {
    if (generalInfo?.partClasses) {
      return generalInfo?.partClasses;
    }
    return [];
  });

  const { isLoading: isLoadingUnitsOfMeasure, data: unitsOfMeasure } = useQuery<
    IDropdownResponse[],
    Error
  >(['getUnitsOfMeasure'], () => getUnitsOfMeasure());

  const { isLoading: isLoadingPartClassParents, data: partClassParents } = useQuery<
    IDropdownResponse[],
    Error
  >(
    ['part-class-parents', selectedPartClassIdFilter],
    async () =>
      await getPartClassParents(
        selectedPartClassIdFilter === null && !selectionsCleared
          ? generalInfo?.partClassId
          : selectedPartClassIdFilter
      ),
    {
      enabled: (!!selectedPartClassIdFilter || !!generalInfo) && !selectionsCleared
    }
  );

  useEffect(() => {
    if (partClassParents) {
      setSelectedValues(partClassParents.slice().reverse());
    }
  }, [partClassParents]);

  const handleFileChange = async (images: any[], files: File[]) => {
    try {
      setUploading(true);

      const firstItem = files[0];
      const formData = new FormData();
      formData.append('Image', firstItem);

      setFileName(firstItem.name);
      let url = URL.createObjectURL(firstItem);
      setUploadedFile(url);
      setFile(firstItem);

      setDirtyForm(true);
      enqueueSnackbar('File uploaded successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error uploading file, please try again.', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  useQuery(['generalInfoFile', partId], async () => {
    if (partId) {
      try {
        const generalInfoFile = await getPartFile(partId);
        if (
          generalInfoFile?.status !== 204 &&
          generalInfoFile?.status >= 200 &&
          generalInfoFile?.status < 300
        ) {
          let url = URL.createObjectURL(generalInfoFile?.data);

          const fileName = generalInfoFile?.headers?.['content-disposition']
            ?.split('filename=')?.[1]
            ?.split(';')?.[0]
            ?.replaceAll('"', '');

          setFileName(fileName);
          setUploadedFile(url);
        }
      } catch (error) {
        const errorMessage = error?.Detail ?? error?.message ?? 'Error fetching image file.';
        enqueueSnackbar(errorMessage, {
          variant: 'error'
        });
      }
    }
  });

  return (
    <>
      {isLoadingGeneralInfo && isLoadingPartClassParents && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingGeneralInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            partClassId: generalInfo?.partClassId ?? '',
            name: generalInfo?.name ?? '',
            unitOfMeasureId: generalInfo?.unitOfMeasureId ?? '',
            quantityInterval: generalInfo?.quantityInterval ?? '',
            isActive:
              generalInfo?.isActive === true ? 'Yes' : generalInfo?.isActive === false ? 'No' : '',
            roundUp:
              generalInfo?.roundUp === true ? 'Yes' : generalInfo?.roundUp === false ? 'No' : '',
            partClasses: generalInfo?.partClasses ?? []
          }}
          validationSchema={GeneralInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IPartGeneralInfo = {
                ...values,
                partClassId: selectedPartClassIdFilter ?? values.partClassId,
                isActive: values.isActive === 'Yes' ? true : false,
                roundUp: values.roundUp === 'Yes' ? true : values.roundUp === '' ? null : false
              };

              await putPartGeneralInfo(parseInt(partId), data);

              if (file) {
                const formData = new FormData();
                formData.append('Image', file);
                await createPartsFile(partId, formData);
              }

              refetchGeneralInfo();
              enqueueSnackbar('Part has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              const errorMessage =
                error?.Detail ?? error?.message ?? 'Something went wrong. Part was not updated.';
              enqueueSnackbar(errorMessage, {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            if (
              selectedPartClassIdFilter !== null &&
              selectedPartClassIdFilter !== generalInfo?.partClassId
            ) {
              dirty = true;
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1.5}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              autoComplete="nope"
                              label="Name"
                              name="name"
                              size="small"
                              value={values.name}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('name', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="unitOfMeasureId"
                              label="Unit of Measure"
                              variant="outlined"
                              id="unitOfMeasureId"
                              disabled={isLoadingUnitsOfMeasure}
                              value={values.unitOfMeasureId}
                              onChange={({ target: { value } }) => {
                                setFieldValue('unitOfMeasureId', value);
                              }}
                              onBlur={handleBlur}
                              error={errors && errors.unitOfMeasureId ? true : false}
                            >
                              {unitsOfMeasure &&
                                unitsOfMeasure.map((units, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={units.value}>
                                      {units.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {touched &&
                              touched.unitOfMeasureId &&
                              errors &&
                              errors.unitOfMeasureId && (
                                <FormHelperText error>Required</FormHelperText>
                              )}
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={1}>
                              <ManagePartClassDropdown
                                required={true}
                                partClassId={null}
                                depth={0}
                                setSelectedPartClassIdFilter={setSelectedPartClassIdFilter}
                                width={3}
                                selectedValues={selectedValues}
                                setSelectedValues={setSelectedValues}
                                setSelectionsCleared={setSelectionsCleared}
                              />
                            </Grid>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Quantity Interval"
                              name="quantityInterval"
                              size="small"
                              value={values.quantityInterval}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('quantityInterval', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              disabled={values.quantityInterval ? false : true}
                              onChange={({ target: { value } }) => {
                                setFieldValue('roundUp', value);
                              }}
                              handleClear={() => setFieldValue('roundUp', '')}
                              variant="outlined"
                              name="rounding"
                              label="Rounding"
                              id="rounding"
                              value={values.roundUp}
                            >
                              {[
                                { value: 'Yes', shorthand: 'Yes', description: 'Yes' },
                                { value: 'No', shorthand: 'No', description: 'No' }
                              ].map((rounding, index) => {
                                return (
                                  <MenuItem key={`${rounding.value}`} value={rounding.value}>
                                    {rounding.description}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={2}>
                            <FormControl component="fieldset">
                              <FormLabel>Is Part Active*</FormLabel>
                              <RadioGroup
                                sx={{
                                  position: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'left',
                                  alignItems: 'start'
                                }}
                                aria-label="isActive"
                                name="isActive"
                                value={values.isActive}
                                onChange={e => setFieldValue('isActive', e.target.value)}
                              >
                                <FormControlLabel value={'Yes'} control={<Radio />} label={'Yes'} />
                                <FormControlLabel value={'No'} control={<Radio />} label={'No'} />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>
                      {uploadedFile && (
                        <>
                          <Grid item xs={12} sm={4} pt={0}>
                            <Typography
                              mb={0.75}
                              sx={{ color: theme.palette.grey[600], fontSize: '1rem' }}
                            >
                              Part Image
                            </Typography>
                            <Box
                              component="img"
                              className={classes.imageFormat}
                              alt="Part Image File"
                              src={uploadedFile}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <a
                              href={uploadedFile}
                              download={fileName || 'uploadedFile.pdf'}
                              className={classes.downloadText}
                            >
                              {fileName || 'Download File'}
                            </a>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={12}>
                        <ManyImagesUpload
                          fileType={ALLOWED_IMAGE_EXTENSIONS.join(', ')}
                          isRequired={false}
                          isDisabled={isUploading}
                          isLoading={isUploading}
                          imageKey={'partFile'}
                          uploadText={'Upload File'}
                          handleFileChange={handleFileChange}
                          images={[]}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup
                    sx={{
                      my: { xs: 0.75, sm: 0 }
                    }}
                  >
                    <Button
                      disabled={(!dirty && !file) || isSubmitting || !isValid}
                      type="submit"
                      startIcon={partId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/parts/manage-parts');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  imageFormat: {
    width: '100%',
    height: 'auto',
    border: `2px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 3px rgba(0,0,0,0.06)'
  },
  downloadText: {
    color: theme.palette.primary.main,
    textDecorationColor: theme.palette.primary.main
  },
  disabledLink: {
    pointerEvents: 'none',
    opacity: 0.5
  }
}));
